import * as THREE from 'three';
import { KartId, ItemType } from '@shared/types';
import { DriftSparks } from './DriftSparks';

interface KartVisual {
  group: THREE.Group;
  bodyGroup: THREE.Group;
  wheels: THREE.Mesh[];
  frontWheels: THREE.Mesh[];
  boostGroup: THREE.Group | null;
  stunGroup: THREE.Group | null;
  driftSparks: DriftSparks | null;
  driftTimer: number;
  // New Mario Kart effect state
  orbitGroup: THREE.Group | null;
  orbitItemCount: number;
  heldItemMesh: THREE.Group | null;
  heldItemType: ItemType | null;
  currentScale: number;
  starHue: number;
  bodyMaterials: THREE.MeshStandardMaterial[];
}

export class KartRenderer {
  private karts: Map<string, KartVisual> = new Map();

  // Shared geometries and materials (reuse across karts)
  private wheelGeo: THREE.CylinderGeometry;
  private wheelMat: THREE.MeshStandardMaterial;
  private wheelHubGeo: THREE.CylinderGeometry;
  private wheelHubMat: THREE.MeshStandardMaterial;

  constructor(private scene: THREE.Scene) {
    this.wheelGeo = new THREE.CylinderGeometry(0.35, 0.35, 0.25, 12);
    this.wheelMat = new THREE.MeshStandardMaterial({
      color: 0x222222,
      roughness: 0.9,
      metalness: 0.1,
    });
    this.wheelHubGeo = new THREE.CylinderGeometry(0.15, 0.15, 0.27, 6);
    this.wheelHubMat = new THREE.MeshStandardMaterial({
      color: 0xaaaaaa,      // Lighter chrome
      roughness: 0.1,       // Very shiny
      metalness: 0.95,      // Nearly full metal
      envMapIntensity: 1.5,
    });
  }

  /** Create a kart visual for a player */
  createKart(id: string, kartId: KartId, color: number): void {
    // Remove existing kart with this id if any
    if (this.karts.has(id)) {
      this.removeKart(id);
    }

    const group = new THREE.Group();
    const bodyGroup = new THREE.Group();

    // Collect body materials so we can manipulate them for star effect
    const bodyMaterials: THREE.MeshStandardMaterial[] = [];

    if (kartId === 'speed_demon') {
      this.buildSpeedDemon(bodyGroup, color, bodyMaterials);
    } else {
      this.buildRoadHugger(bodyGroup, color, bodyMaterials);
    }
    group.add(bodyGroup);

    const wheelPositions = kartId === 'speed_demon'
      ? this.getSpeedDemonWheelPositions()
      : this.getRoadHuggerWheelPositions();

    const wheels: THREE.Mesh[] = [];
    const frontWheels: THREE.Mesh[] = [];

    for (let i = 0; i < 4; i++) {
      const wp = wheelPositions[i];
      const wheelGroup = this.buildWheel(kartId === 'speed_demon');
      wheelGroup.position.set(wp.x, wp.y, wp.z);
      // Rotate 90 degrees so the cylinder axis points sideways
      wheelGroup.rotation.z = Math.PI / 2;
      group.add(wheelGroup);
      wheels.push(wheelGroup);
      if (i < 2) frontWheels.push(wheelGroup); // first two are front wheels
    }

    // ── Drift sparks ──
    const rearLeft = wheelPositions[2]; // rear-left
    const rearRight = wheelPositions[3]; // rear-right
    const driftSparks = new DriftSparks(rearLeft, rearRight);
    // Spark particles live in world space (so they stay behind when kart moves)
    this.scene.add(driftSparks.worldGroup);
    // Ground streaks live in kart-local space (move with the kart)
    group.add(driftSparks.kartGroup);

    this.scene.add(group);
    this.karts.set(id, {
      group,
      bodyGroup,
      wheels,
      frontWheels,
      boostGroup: null,
      stunGroup: null,
      driftSparks,
      driftTimer: 0,
      orbitGroup: null,
      orbitItemCount: 0,
      heldItemMesh: null,
      heldItemType: null,
      currentScale: 1.0,
      starHue: 0,
      bodyMaterials,
    });
  }

  // ────────────────────────────────────────────────────────────
  // Speed Demon Build
  // ────────────────────────────────────────────────────────────

  private buildSpeedDemon(parent: THREE.Group, color: number, bodyMats: THREE.MeshStandardMaterial[]): void {
    const bodyMat = new THREE.MeshStandardMaterial({
      color,
      roughness: 0.25,     // More polished
      metalness: 0.45,     // More metallic
      envMapIntensity: 1.2, // Strong environment reflection
    });

    // Rim/fresnel highlight — makes the kart "pop" against the background
    bodyMat.onBeforeCompile = (shader) => {
      shader.vertexShader = `varying vec3 vWorldPosition;\n` + shader.vertexShader;
      shader.vertexShader = shader.vertexShader.replace(
        '#include <worldpos_vertex>',
        `#include <worldpos_vertex>
        vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;`
      );
      shader.fragmentShader = `varying vec3 vWorldPosition;\n` + shader.fragmentShader;
      shader.fragmentShader = shader.fragmentShader.replace(
        'vec3 outgoingLight = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + reflectedLight.directSpecular + reflectedLight.indirectSpecular + totalEmissiveRadiance;',
        `vec3 outgoingLight = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + reflectedLight.directSpecular + reflectedLight.indirectSpecular + totalEmissiveRadiance;
        // Rim light: bright edge glow facing away from camera
        vec3 viewDir = normalize(cameraPosition - vWorldPosition);
        float rim = 1.0 - max(0.0, dot(normalize(normal), viewDir));
        rim = pow(rim, 3.0);
        outgoingLight += rim * 0.5 * diffuseColor.rgb;`
      );
    };

    bodyMats.push(bodyMat);

    const accentMat = new THREE.MeshStandardMaterial({
      color: 0x222222,
      roughness: 0.5,
      metalness: 0.4,
    });
    const glassMat = new THREE.MeshStandardMaterial({
      color: 0x99ccff,
      roughness: 0.0,          // Mirror-smooth
      metalness: 0.1,
      transparent: true,
      opacity: 0.4,
      envMapIntensity: 2.0,    // Strong reflections
      side: THREE.FrontSide,
    });

    // Main body: long, low, sleek box
    const mainGeo = new THREE.BoxGeometry(2.8, 0.45, 1.2);
    const mainBody = new THREE.Mesh(mainGeo, bodyMat);
    mainBody.position.y = 0.55;
    mainBody.castShadow = true;
    parent.add(mainBody);

    // Front nose: tapered wedge using a scaled box
    const noseGeo = new THREE.BoxGeometry(1.0, 0.3, 1.0);
    const nose = new THREE.Mesh(noseGeo, bodyMat);
    nose.position.set(1.7, 0.45, 0);
    nose.scale.set(1, 1, 0.8);
    nose.castShadow = true;
    parent.add(nose);

    // Front aero splitter
    const splitterGeo = new THREE.BoxGeometry(0.6, 0.08, 1.3);
    const splitter = new THREE.Mesh(splitterGeo, accentMat);
    splitter.position.set(2.1, 0.32, 0);
    parent.add(splitter);

    // Cockpit: slightly recessed area
    const cockpitGeo = new THREE.BoxGeometry(0.8, 0.35, 0.8);
    const cockpit = new THREE.Mesh(cockpitGeo, accentMat);
    cockpit.position.set(0, 0.85, 0);
    cockpit.castShadow = true;
    parent.add(cockpit);

    // Windshield: angled glass
    const windshieldGeo = new THREE.BoxGeometry(0.05, 0.35, 0.7);
    const windshield = new THREE.Mesh(windshieldGeo, glassMat);
    windshield.position.set(0.4, 0.9, 0);
    windshield.rotation.z = -0.3;
    parent.add(windshield);

    // Side intakes: small indented boxes
    for (const zSide of [-1, 1]) {
      const intakeGeo = new THREE.BoxGeometry(0.6, 0.2, 0.1);
      const intake = new THREE.Mesh(intakeGeo, accentMat);
      intake.position.set(0.8, 0.5, zSide * 0.65);
      parent.add(intake);
    }

    // Rear spoiler: two supports + wing
    for (const zSide of [-0.4, 0.4]) {
      const supportGeo = new THREE.BoxGeometry(0.08, 0.5, 0.08);
      const support = new THREE.Mesh(supportGeo, accentMat);
      support.position.set(-1.2, 1.0, zSide);
      parent.add(support);
    }
    const wingGeo = new THREE.BoxGeometry(0.5, 0.06, 1.2);
    const wing = new THREE.Mesh(wingGeo, accentMat);
    wing.position.set(-1.2, 1.3, 0);
    wing.castShadow = true;
    parent.add(wing);

    // Rear diffuser
    const diffuserGeo = new THREE.BoxGeometry(0.3, 0.15, 1.1);
    const diffuser = new THREE.Mesh(diffuserGeo, accentMat);
    diffuser.position.set(-1.55, 0.4, 0);
    parent.add(diffuser);

    // Exhaust pipes: two small cylinders
    for (const zSide of [-0.3, 0.3]) {
      const exhaustGeo = new THREE.CylinderGeometry(0.08, 0.1, 0.3, 8);
      const exhaustMat = new THREE.MeshStandardMaterial({
        color: 0x666666,
        roughness: 0.3,
        metalness: 0.8,
      });
      const exhaust = new THREE.Mesh(exhaustGeo, exhaustMat);
      exhaust.position.set(-1.6, 0.45, zSide);
      exhaust.rotation.z = Math.PI / 2;
      parent.add(exhaust);
    }

    // Headlights: two small emissive spheres (high intensity for bloom)
    for (const zSide of [-0.35, 0.35]) {
      const lightGeo = new THREE.SphereGeometry(0.1, 8, 8);
      const lightMat = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        emissive: 0xffffff,
        emissiveIntensity: 3.0,  // Very bright for bloom
        roughness: 0.1,
        metalness: 0.0,
      });
      const light = new THREE.Mesh(lightGeo, lightMat);
      light.position.set(2.15, 0.5, zSide);
      parent.add(light);
    }

    // Tail lights: red emissive spheres at the rear
    for (const zSide of [-0.3, 0.3]) {
      const taillightGeo = new THREE.SphereGeometry(0.1, 8, 6);
      const taillightMat = new THREE.MeshStandardMaterial({
        color: 0xff2200,
        emissive: 0xff1100,
        emissiveIntensity: 2.5,
        roughness: 0.1,
        metalness: 0.0,
      });
      const taillight = new THREE.Mesh(taillightGeo, taillightMat);
      taillight.position.set(-1.5, 0.5, zSide);
      parent.add(taillight);
    }

    // Racing stripe: thin colored line on top
    const stripeGeo = new THREE.BoxGeometry(3.2, 0.02, 0.15);
    const stripeMat = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.3,
      metalness: 0.1,
    });
    const stripe = new THREE.Mesh(stripeGeo, stripeMat);
    stripe.position.set(0.3, 0.78, 0);
    parent.add(stripe);
  }

  private getSpeedDemonWheelPositions(): Array<{ x: number; y: number; z: number }> {
    return [
      { x: 1.1, y: 0.35, z: -0.7 }, // front-left
      { x: 1.1, y: 0.35, z: 0.7 }, // front-right
      { x: -1.1, y: 0.35, z: -0.7 }, // rear-left
      { x: -1.1, y: 0.35, z: 0.7 }, // rear-right
    ];
  }

  // ────────────────────────────────────────────────────────────
  // Road Hugger Build
  // ────────────────────────────────────────────────────────────

  private buildRoadHugger(parent: THREE.Group, color: number, bodyMats: THREE.MeshStandardMaterial[]): void {
    const bodyMat = new THREE.MeshStandardMaterial({
      color,
      roughness: 0.55,      // Slightly less matte
      metalness: 0.3,       // More metallic
      envMapIntensity: 0.8,
    });

    // Rim/fresnel highlight — makes the kart "pop" against the background
    bodyMat.onBeforeCompile = (shader) => {
      shader.vertexShader = `varying vec3 vWorldPosition;\n` + shader.vertexShader;
      shader.vertexShader = shader.vertexShader.replace(
        '#include <worldpos_vertex>',
        `#include <worldpos_vertex>
        vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;`
      );
      shader.fragmentShader = `varying vec3 vWorldPosition;\n` + shader.fragmentShader;
      shader.fragmentShader = shader.fragmentShader.replace(
        'vec3 outgoingLight = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + reflectedLight.directSpecular + reflectedLight.indirectSpecular + totalEmissiveRadiance;',
        `vec3 outgoingLight = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + reflectedLight.directSpecular + reflectedLight.indirectSpecular + totalEmissiveRadiance;
        // Rim light: bright edge glow facing away from camera
        vec3 viewDir = normalize(cameraPosition - vWorldPosition);
        float rim = 1.0 - max(0.0, dot(normalize(normal), viewDir));
        rim = pow(rim, 3.0);
        outgoingLight += rim * 0.5 * diffuseColor.rgb;`
      );
    };

    bodyMats.push(bodyMat);

    const accentMat = new THREE.MeshStandardMaterial({
      color: 0xdddddd,
      roughness: 0.4,
      metalness: 0.3,
    });
    const darkMat = new THREE.MeshStandardMaterial({
      color: 0x333333,
      roughness: 0.5,
      metalness: 0.4,
    });
    const glassMat = new THREE.MeshStandardMaterial({
      color: 0x99ccff,
      roughness: 0.0,          // Mirror-smooth
      metalness: 0.1,
      transparent: true,
      opacity: 0.4,
      envMapIntensity: 2.0,    // Strong reflections
      side: THREE.FrontSide,
    });

    // Main body: chunky, wide, tall
    const mainGeo = new THREE.BoxGeometry(2.2, 0.7, 1.6);
    const mainBody = new THREE.Mesh(mainGeo, bodyMat);
    mainBody.position.y = 0.65;
    mainBody.castShadow = true;
    parent.add(mainBody);

    // Hood: slightly raised front section
    const hoodGeo = new THREE.BoxGeometry(0.8, 0.15, 1.4);
    const hood = new THREE.Mesh(hoodGeo, bodyMat);
    hood.position.set(1.3, 0.85, 0);
    hood.castShadow = true;
    parent.add(hood);

    // Cabin: raised box for the driver area
    const cabinGeo = new THREE.BoxGeometry(1.0, 0.5, 1.3);
    const cabin = new THREE.Mesh(cabinGeo, bodyMat);
    cabin.position.set(-0.1, 1.25, 0);
    cabin.castShadow = true;
    parent.add(cabin);

    // Windshield
    const windshieldGeo = new THREE.BoxGeometry(0.06, 0.45, 1.15);
    const windshield = new THREE.Mesh(windshieldGeo, glassMat);
    windshield.position.set(0.4, 1.25, 0);
    windshield.rotation.z = -0.2;
    parent.add(windshield);

    // Rear window
    const rearWindowGeo = new THREE.BoxGeometry(0.06, 0.35, 1.0);
    const rearWindow = new THREE.Mesh(rearWindowGeo, glassMat);
    rearWindow.position.set(-0.55, 1.2, 0);
    rearWindow.rotation.z = 0.2;
    parent.add(rearWindow);

    // Roll cage: tubes over the cabin
    const cageMat = accentMat;
    // Top bar
    const topBarGeo = new THREE.CylinderGeometry(0.06, 0.06, 1.4, 6);
    const topBar = new THREE.Mesh(topBarGeo, cageMat);
    topBar.position.set(-0.1, 1.55, 0);
    topBar.rotation.x = Math.PI / 2;
    parent.add(topBar);
    // Front uprights
    for (const zSide of [-0.6, 0.6]) {
      const uprightGeo = new THREE.CylinderGeometry(0.06, 0.06, 0.55, 6);
      const upright = new THREE.Mesh(uprightGeo, cageMat);
      upright.position.set(0.35, 1.25, zSide);
      upright.rotation.z = -0.25;
      parent.add(upright);
    }
    // Rear uprights
    for (const zSide of [-0.6, 0.6]) {
      const uprightGeo = new THREE.CylinderGeometry(0.06, 0.06, 0.55, 6);
      const upright = new THREE.Mesh(uprightGeo, cageMat);
      upright.position.set(-0.55, 1.25, zSide);
      upright.rotation.z = 0.25;
      parent.add(upright);
    }

    // Bull bar at front
    const bullBarGeo = new THREE.BoxGeometry(0.12, 0.35, 1.7);
    const bullBar = new THREE.Mesh(bullBarGeo, accentMat);
    bullBar.position.set(1.2, 0.5, 0);
    bullBar.castShadow = true;
    parent.add(bullBar);

    // Side steps
    for (const zSide of [-0.85, 0.85]) {
      const stepGeo = new THREE.BoxGeometry(1.8, 0.08, 0.2);
      const step = new THREE.Mesh(stepGeo, darkMat);
      step.position.set(0, 0.32, zSide);
      parent.add(step);
    }

    // Rear bumper
    const rearBumperGeo = new THREE.BoxGeometry(0.15, 0.3, 1.6);
    const rearBumper = new THREE.Mesh(rearBumperGeo, darkMat);
    rearBumper.position.set(-1.2, 0.48, 0);
    parent.add(rearBumper);

    // Headlights: two larger emissive rectangles (high intensity for bloom)
    for (const zSide of [-0.5, 0.5]) {
      const lightGeo = new THREE.BoxGeometry(0.05, 0.15, 0.2);
      const lightMat = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        emissive: 0xffffff,
        emissiveIntensity: 3.0,  // Very bright for bloom
        roughness: 0.1,
        metalness: 0.0,
      });
      const light = new THREE.Mesh(lightGeo, lightMat);
      light.position.set(1.25, 0.65, zSide);
      parent.add(light);
    }

    // Tail lights (brighter for bloom)
    for (const zSide of [-0.55, 0.55]) {
      const lightGeo = new THREE.BoxGeometry(0.05, 0.12, 0.18);
      const lightMat = new THREE.MeshStandardMaterial({
        color: 0xff2200,
        emissive: 0xff1100,
        emissiveIntensity: 2.5,
        roughness: 0.2,
        metalness: 0.0,
      });
      const light = new THREE.Mesh(lightGeo, lightMat);
      light.position.set(-1.18, 0.65, zSide);
      parent.add(light);
    }

    // Fender flares: slightly wider sections over the wheels
    for (const xSide of [0.9, -0.9]) {
      for (const zSide of [-0.85, 0.85]) {
        const flareGeo = new THREE.BoxGeometry(0.6, 0.15, 0.15);
        const flare = new THREE.Mesh(flareGeo, bodyMat);
        flare.position.set(xSide, 0.35, zSide);
        parent.add(flare);
      }
    }
  }

  private getRoadHuggerWheelPositions(): Array<{ x: number; y: number; z: number }> {
    return [
      { x: 0.9, y: 0.35, z: -0.9 }, // front-left
      { x: 0.9, y: 0.35, z: 0.9 }, // front-right
      { x: -0.9, y: 0.35, z: -0.9 }, // rear-left
      { x: -0.9, y: 0.35, z: 0.9 }, // rear-right
    ];
  }

  // ────────────────────────────────────────────────────────────
  // Wheel Build
  // ────────────────────────────────────────────────────────────

  private buildWheel(isSpeedDemon: boolean): THREE.Mesh {
    const wheel = new THREE.Mesh(this.wheelGeo, this.wheelMat);
    wheel.castShadow = true;

    // Hub cap
    const hub = new THREE.Mesh(this.wheelHubGeo, this.wheelHubMat);
    wheel.add(hub);

    // Speed Demon gets slightly wider wheels
    if (isSpeedDemon) {
      wheel.scale.set(1, 1.2, 1);
    }

    return wheel;
  }

  // ────────────────────────────────────────────────────────────
  // Boost Effect
  // ────────────────────────────────────────────────────────────

  private createBoostEffect(): THREE.Group {
    const group = new THREE.Group();

    // Two flame cones behind the kart
    for (const zOff of [-0.3, 0.3]) {
      // Inner flame (bright yellow-white)
      const innerGeo = new THREE.ConeGeometry(0.15, 1.2, 8);
      const innerMat = new THREE.MeshStandardMaterial({
        color: 0xffff44,
        emissive: 0xffaa00,
        emissiveIntensity: 2.0,
        transparent: true,
        opacity: 0.9,
      });
      const inner = new THREE.Mesh(innerGeo, innerMat);
      inner.position.set(-1.7, 0.45, zOff);
      inner.rotation.z = -Math.PI / 2; // point backward
      group.add(inner);

      // Outer flame (orange-red, larger)
      const outerGeo = new THREE.ConeGeometry(0.25, 1.8, 8);
      const outerMat = new THREE.MeshStandardMaterial({
        color: 0xff4400,
        emissive: 0xff2200,
        emissiveIntensity: 1.5,
        transparent: true,
        opacity: 0.6,
      });
      const outer = new THREE.Mesh(outerGeo, outerMat);
      outer.position.set(-2.0, 0.45, zOff);
      outer.rotation.z = -Math.PI / 2;
      group.add(outer);
    }

    // Speed lines: thin long boxes that trail behind
    for (let i = 0; i < 4; i++) {
      const lineGeo = new THREE.BoxGeometry(2.5 + Math.random(), 0.03, 0.03);
      const lineMat = new THREE.MeshStandardMaterial({
        color: 0xffddaa,
        emissive: 0xffaa44,
        emissiveIntensity: 1.0,
        transparent: true,
        opacity: 0.4,
      });
      const line = new THREE.Mesh(lineGeo, lineMat);
      line.position.set(
        -2.5 - Math.random() * 1.5,
        0.4 + (Math.random() - 0.5) * 0.4,
        (Math.random() - 0.5) * 0.8
      );
      group.add(line);
    }

    return group;
  }

  // ────────────────────────────────────────────────────────────
  // Stun Effect
  // ────────────────────────────────────────────────────────────

  private createStunEffect(): THREE.Group {
    const group = new THREE.Group();

    // Spinning stars above the kart
    const starCount = 5;
    const starMat = new THREE.MeshStandardMaterial({
      color: 0xffff00,
      emissive: 0xffff00,
      emissiveIntensity: 1.0,
    });

    for (let i = 0; i < starCount; i++) {
      const angle = (i / starCount) * Math.PI * 2;
      const starGroup = new THREE.Group();

      // Simple star: two intersecting boxes
      const bar1Geo = new THREE.BoxGeometry(0.4, 0.1, 0.1);
      const bar1 = new THREE.Mesh(bar1Geo, starMat);
      starGroup.add(bar1);

      const bar2Geo = new THREE.BoxGeometry(0.1, 0.1, 0.4);
      const bar2 = new THREE.Mesh(bar2Geo, starMat);
      starGroup.add(bar2);

      const bar3Geo = new THREE.BoxGeometry(0.28, 0.1, 0.28);
      const bar3 = new THREE.Mesh(bar3Geo, starMat);
      bar3.rotation.y = Math.PI / 4;
      starGroup.add(bar3);

      starGroup.position.set(
        Math.cos(angle) * 1.0,
        2.0,
        Math.sin(angle) * 1.0
      );
      starGroup.userData.orbitAngle = angle;

      group.add(starGroup);
    }

    // Small sparks
    for (let i = 0; i < 3; i++) {
      const sparkGeo = new THREE.SphereGeometry(0.08, 4, 4);
      const sparkMat = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        emissive: 0xffffaa,
        emissiveIntensity: 2.0,
      });
      const spark = new THREE.Mesh(sparkGeo, sparkMat);
      spark.position.set(
        (Math.random() - 0.5) * 1.5,
        1.5 + Math.random() * 0.5,
        (Math.random() - 0.5) * 1.5
      );
      group.add(spark);
    }

    return group;
  }

  // ────────────────────────────────────────────────────────────
  // Orbit Items Effect (triple shells / bananas orbiting the kart)
  // ────────────────────────────────────────────────────────────

  private createOrbitGroup(count: number): THREE.Group {
    const group = new THREE.Group();
    const shellGeo = new THREE.SphereGeometry(0.3, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2);
    const shellMat = new THREE.MeshStandardMaterial({
      color: 0x22cc44,
      emissive: 0x118822,
      emissiveIntensity: 0.3,
      roughness: 0.4,
    });

    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const item = new THREE.Mesh(shellGeo, shellMat);
      item.position.set(
        Math.cos(angle) * 2.5,
        0.6,
        Math.sin(angle) * 2.5
      );
      item.userData.orbitAngle = angle;
      group.add(item);
    }

    return group;
  }

  // ────────────────────────────────────────────────────────────
  // Held Item Behind
  // ────────────────────────────────────────────────────────────

  private createHeldItemMesh(itemType: ItemType): THREE.Group {
    const group = new THREE.Group();
    let meshColor = 0x22cc44;

    switch (itemType) {
      case ItemType.GreenShell:
      case ItemType.TripleGreenShell:
        meshColor = 0x22cc44;
        break;
      case ItemType.RedShell:
      case ItemType.TripleRedShell:
        meshColor = 0xcc2222;
        break;
      case ItemType.Banana:
      case ItemType.TripleBanana:
        meshColor = 0xddcc22;
        break;
      default:
        meshColor = 0x22cc44;
    }

    if (itemType === ItemType.Banana || itemType === ItemType.TripleBanana) {
      // Banana shape
      const bananaGeo = new THREE.CylinderGeometry(0.15, 0.2, 0.5, 8);
      const bananaMat = new THREE.MeshStandardMaterial({
        color: meshColor,
        emissive: 0x554400,
        emissiveIntensity: 0.2,
        roughness: 0.5,
      });
      const seg1 = new THREE.Mesh(bananaGeo, bananaMat);
      seg1.position.set(-0.1, 0, 0);
      seg1.rotation.z = 0.3;
      group.add(seg1);
      const seg2 = new THREE.Mesh(bananaGeo, bananaMat);
      seg2.position.set(0.1, 0, 0);
      seg2.rotation.z = -0.3;
      group.add(seg2);
    } else {
      // Shell shape
      const shellGeo = new THREE.SphereGeometry(0.35, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2);
      const shellMat = new THREE.MeshStandardMaterial({
        color: meshColor,
        emissive: meshColor,
        emissiveIntensity: 0.2,
        roughness: 0.4,
      });
      const shell = new THREE.Mesh(shellGeo, shellMat);
      group.add(shell);
      const discGeo = new THREE.CircleGeometry(0.35, 10);
      const discMat = new THREE.MeshStandardMaterial({ color: 0xeeeecc, roughness: 0.6 });
      const disc = new THREE.Mesh(discGeo, discMat);
      disc.rotation.x = -Math.PI / 2;
      group.add(disc);
    }

    return group;
  }

  // ────────────────────────────────────────────────────────────
  // Update
  // ────────────────────────────────────────────────────────────

  /** Update kart position, rotation, and effects */
  updateKart(
    id: string,
    x: number,
    y: number,
    z: number,
    yaw: number,
    steering: number,
    speed: number,
    isBoosting: boolean,
    isStunned: boolean,
    isDrifting: boolean,
    dt: number,
    // New optional parameters for Mario Kart mechanics
    shrinkTimer?: number,
    starTimer?: number,
    orbitItems?: number,
    heldItemBehind?: ItemType | null,
    trickBoosting?: boolean
  ): void {
    const kart = this.karts.get(id);
    if (!kart) return;

    // Position
    kart.group.position.set(x, y, z);

    // Orientation:
    // yaw = 0 means facing +X in game coords.
    // Our kart meshes are built facing +X (forward along positive X).
    // In Three.js, applying rotation.y = theta transforms local +X to world:
    //   world_x = cos(theta), world_z = -sin(theta)
    // For this to match physics forward (cos(yaw), sin(yaw)):
    //   cos(theta) = cos(yaw) and -sin(theta) = sin(yaw) -> theta = -yaw
    kart.group.rotation.y = -yaw;

    // Spin wheels based on speed
    const wheelSpin = speed * 0.3 * dt;
    for (const w of kart.wheels) {
      w.rotation.y += wheelSpin;
    }

    // Steer front wheels
    for (const fw of kart.frontWheels) {
      fw.rotation.x = -steering * 0.5;
    }

    // Lean into turns
    kart.bodyGroup.rotation.z = -steering * 0.15;
    // Slight pitch when accelerating / braking
    kart.bodyGroup.rotation.x = -speed * 0.002;

    // ── Shrink effect (B6) ──
    const targetShrink = (shrinkTimer !== undefined && shrinkTimer > 0) ? 0.5 : 1.0;
    kart.currentScale += (targetShrink - kart.currentScale) * Math.min(1, 8 * dt);
    kart.group.scale.setScalar(kart.currentScale);

    // ── Star rainbow effect (B7) ──
    if (starTimer !== undefined && starTimer > 0) {
      kart.starHue += dt * 360; // cycle through hues
      if (kart.starHue > 360) kart.starHue -= 360;
      const color = new THREE.Color();
      color.setHSL(kart.starHue / 360, 1.0, 0.5);
      for (const mat of kart.bodyMaterials) {
        mat.emissive.copy(color);
        mat.emissiveIntensity = 1.5;
      }
    } else if (kart.starHue !== 0) {
      // Reset emissive when star wears off
      kart.starHue = 0;
      for (const mat of kart.bodyMaterials) {
        mat.emissive.setHex(0x000000);
        mat.emissiveIntensity = 0;
      }
    }

    // ── Orbiting items (B5) ──
    const desiredOrbit = orbitItems ?? 0;
    if (desiredOrbit > 0 && (!kart.orbitGroup || kart.orbitItemCount !== desiredOrbit)) {
      // Recreate orbit group with correct count
      if (kart.orbitGroup) {
        kart.group.remove(kart.orbitGroup);
        this.disposeGroup(kart.orbitGroup);
      }
      kart.orbitGroup = this.createOrbitGroup(desiredOrbit);
      kart.group.add(kart.orbitGroup);
      kart.orbitItemCount = desiredOrbit;
    }
    if (desiredOrbit === 0 && kart.orbitGroup) {
      kart.group.remove(kart.orbitGroup);
      this.disposeGroup(kart.orbitGroup);
      kart.orbitGroup = null;
      kart.orbitItemCount = 0;
    }
    if (kart.orbitGroup) {
      // Rotate orbit items around the kart
      const time = performance.now() * 0.002;
      for (const child of kart.orbitGroup.children) {
        if (child.userData.orbitAngle !== undefined) {
          const angle = child.userData.orbitAngle + time;
          child.position.x = Math.cos(angle) * 2.5;
          child.position.z = Math.sin(angle) * 2.5;
          child.rotation.y = time * 2;
        }
      }
    }

    // ── Held item behind (B11) ──
    const desiredHeld = heldItemBehind ?? null;
    if (desiredHeld !== null && (kart.heldItemType !== desiredHeld || !kart.heldItemMesh)) {
      // Create or replace held item mesh
      if (kart.heldItemMesh) {
        kart.group.remove(kart.heldItemMesh);
        this.disposeGroup(kart.heldItemMesh);
      }
      kart.heldItemMesh = this.createHeldItemMesh(desiredHeld);
      kart.heldItemMesh.position.set(-2.0, 0.4, 0); // behind the kart in local space
      kart.group.add(kart.heldItemMesh);
      kart.heldItemType = desiredHeld;
    }
    if (desiredHeld === null && kart.heldItemMesh) {
      kart.group.remove(kart.heldItemMesh);
      this.disposeGroup(kart.heldItemMesh);
      kart.heldItemMesh = null;
      kart.heldItemType = null;
    }

    // ── Trick animation (A4) ──
    if (trickBoosting && y > 0.5) {
      // Front flip on the Z axis (different from barrel roll on X)
      const time = performance.now() * 0.008;
      kart.bodyGroup.rotation.z += Math.sin(time) * 0.15;
    }

    // ── Boost effect ──
    if (isBoosting && !kart.boostGroup) {
      kart.boostGroup = this.createBoostEffect();
      kart.group.add(kart.boostGroup);
    }
    if (!isBoosting && kart.boostGroup) {
      kart.group.remove(kart.boostGroup);
      this.disposeGroup(kart.boostGroup);
      kart.boostGroup = null;
    }
    if (kart.boostGroup) {
      // Animate flame flicker
      const time = performance.now() * 0.01;
      kart.boostGroup.children.forEach((child, i) => {
        const scaleFlicker = 0.8 + Math.sin(time + i * 2) * 0.3;
        child.scale.set(scaleFlicker, scaleFlicker, scaleFlicker);
      });
    }

    // ── Stun effect ──
    if (isStunned && !kart.stunGroup) {
      kart.stunGroup = this.createStunEffect();
      kart.group.add(kart.stunGroup);
    }
    if (!isStunned && kart.stunGroup) {
      kart.group.remove(kart.stunGroup);
      this.disposeGroup(kart.stunGroup);
      kart.stunGroup = null;
    }
    if (kart.stunGroup) {
      // Orbit the stars around the kart
      const time = performance.now() * 0.003;
      kart.stunGroup.children.forEach((child) => {
        if (child.userData.orbitAngle !== undefined) {
          const angle = child.userData.orbitAngle + time;
          child.position.x = Math.cos(angle) * 1.0;
          child.position.z = Math.sin(angle) * 1.0;
          child.rotation.y = time * 2;
        }
      });
    }

    // ── Drift sparks ──
    if (kart.driftSparks) {
      // Track drift duration
      if (isDrifting) {
        kart.driftTimer += dt;
      } else {
        kart.driftTimer = 0;
      }

      kart.driftSparks.update(
        dt,
        isDrifting,
        kart.driftTimer,
        speed,
        kart.group.position,
        yaw
      );
    }
  }

  /** Apply barrel roll rotation to a kart's body group */
  applyBarrelRoll(id: string, progress: number): void {
    const kart = this.karts.get(id);
    if (!kart) return;
    // Smooth ease-in-out using sine curve for satisfying feel
    const angle = progress * Math.PI * 2;
    kart.bodyGroup.rotation.x += angle;
  }

  /** Remove a kart from the scene */
  removeKart(id: string): void {
    const kart = this.karts.get(id);
    if (!kart) return;

    if (kart.boostGroup) {
      kart.group.remove(kart.boostGroup);
      this.disposeGroup(kart.boostGroup);
    }
    if (kart.stunGroup) {
      kart.group.remove(kart.stunGroup);
      this.disposeGroup(kart.stunGroup);
    }
    if (kart.orbitGroup) {
      kart.group.remove(kart.orbitGroup);
      this.disposeGroup(kart.orbitGroup);
    }
    if (kart.heldItemMesh) {
      kart.group.remove(kart.heldItemMesh);
      this.disposeGroup(kart.heldItemMesh);
    }
    if (kart.driftSparks) {
      this.scene.remove(kart.driftSparks.worldGroup);
      kart.group.remove(kart.driftSparks.kartGroup);
      kart.driftSparks.dispose();
    }

    // Reset star emissive on materials before disposal
    for (const mat of kart.bodyMaterials) {
      mat.emissive.setHex(0x000000);
      mat.emissiveIntensity = 0;
    }

    this.scene.remove(kart.group);
    this.disposeGroup(kart.group);
    this.karts.delete(id);
  }

  /** Remove all karts */
  clearAll(): void {
    for (const id of this.karts.keys()) {
      this.removeKart(id);
    }
    this.karts.clear();
  }

  /** Check if a kart visual exists */
  hasKart(id: string): boolean {
    return this.karts.has(id);
  }

  /** Dispose shared resources */
  dispose(): void {
    this.clearAll();
    this.wheelGeo.dispose();
    this.wheelMat.dispose();
    this.wheelHubGeo.dispose();
    this.wheelHubMat.dispose();
  }

  // ────────────────────────────────────────────────────────────
  // Helpers
  // ────────────────────────────────────────────────────────────

  private disposeGroup(group: THREE.Object3D): void {
    group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        // Don't dispose shared wheel geometry/material
        if (child.geometry && child.geometry !== this.wheelGeo && child.geometry !== this.wheelHubGeo) {
          child.geometry.dispose();
        }
        if (child.material) {
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          for (const m of mats) {
            if (m !== this.wheelMat && m !== this.wheelHubMat) {
              m.dispose();
            }
          }
        }
      }
    });
  }
}
